<i18n src="./resources/locales.json"></i18n>

<template>
  <v-card
    id="sms-send-new-message-input-container"
    flat
    tile
    color="surface2"
    class="footer-conversation input-area"
    @dragleave="preventDefault"
    @dragover="preventDefault"
    @dragenter="preventDefault"
    @drop="dropLoadFiles"
  >
    <div class="max-input-height">
      <SmsTextFieldComponent
        ref="textMessageInput"
        stick-to-bottom
        :attachedFile="attachedFile"
        :disabled="disabled"
        :isLoading="isLoading"
        @sendMessage="_sendMessage"
      />
    </div>
    <SmsActionsFooter
      :sendMessageConversation="_sendMessage"
      :disabled="disabled"
      :groupMode="newMessageGroupMode"
      @emoji-select="handleEmojiSelect"
    />
    <SmsErrorMessageDialog
      ref="errorMessageDialog"
    />
  </v-card>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapActions, mapGetters } from 'vuex';
import SmsActionsFooter from './SmsActionsFooter';
import SmsTextFieldComponent from './SmsFooterComponents/SmsTextFieldComponent';
import SmsErrorMessageDialog from './SmsFooterComponents/SmsErrorMessageDialog';
import ValidationMixin from '@/mixins/ValidationMixin';
import ToastMixin from '@/mixins/ToastMixin';
import Message from '@/classes/v2/Message';

export default {
  name: 'SmsConversationFooter',

  components: { SmsActionsFooter, SmsTextFieldComponent, SmsErrorMessageDialog },

  mixins: [ValidationMixin, ToastMixin],

  props: {
    maxSize: {
      type: Number,
      default: 598
    },
    changeAttachedContent: {type: Function},
    updateConversation: { type: Function },
    newMessageGroupMode: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      contactSelectionOpen: false,
      sourceImage: {
        name: '',
        size: 0,
        type: '',
        sourceImageUrl: ''
      },
      attachedFile: {},
      blobFile: null,

      isLoading: false
    };
  },

  beforeDestroy() {
    this.$root.$off('select-gif');
    this.$root.$off('on-load-file');
    this.$root.$off('remove-attached');
    this.$root.$off('focus-conversation-input');
    this.$root.$off('respond-new-message-recipients');
    this.$root.$off('add-file-to-conversation-footer');
  },

  computed: {
    ...mapGetters('user', ['activeNumber', 'activeProvider']),
    ...mapGetters('conversationsV2', [
      'activeConversation',
      'getConversationById',
    ]),

    disabled() {
      return this.activeNumber?.isBlocked(this.activeConversation?.contactNumber);
    },

    isMultimediaMessage() {
      return this.attachedFile.hasFile;
    }
  },

  mounted() {
    this.$root.$on('select-gif', this.selectedGif);
    this.$root.$on('on-load-file', this.loadFile);
    this.$root.$on('remove-attached', this.clearLists);
    this.$root.$on('focus-conversation-input', this.focusConversationInput);
    this.$root.$on('respond-new-message-recipients', this.sendNewMessage);
    this.$root.$on('add-file-to-conversation-footer', this.addFile);
  },

  watch: {
    async activeConversation() {
      await this.$nextTick();
      this.$root.$emit('focus-conversation-input');
    }
  },

  methods: {
    ...mapActions('conversationsV2', [
      'setActiveConversationId',
      'upsertMessage',
      'sendSMS',
      'sendMMS'
    ]),

    focusConversationInput() {
      this.$refs.textMessageInput.focus();
    },

    _sendMessage() {
      if (!this.hasMessageData()) return;
      let messageText = this.$refs.textMessageInput.messageText;

      if (this.$refs.textMessageInput.isTextOverflowed) {
        return this.$refs.textMessageInput.showOverflowWarning();
      }

      if (!this.activeConversation) {
        this.$root.$emit('request-new-message-recipients');
      } else if (this.isMultimediaMessage) {
        this.sendMMSMessage(messageText);
      } else {
        this.sendTextMessage(messageText);
      }

      this.$refs.textMessageInput.messageText = '';
      this.clearLists();
    },

    /**
     * Sends a new message via event emit/response component communication pattern
     * @param {Array} destinationList
     */
    sendNewMessage(destinationList)  {
      let messageText = this.$refs.textMessageInput.messageText;
      destinationList = destinationList.sort();

      if (destinationList.length === 1 || !this.newMessageGroupMode) {
        const conversationId = destinationList.sort()[0];
        if (conversationId.includes('+1')) {
          destinationList[0] = conversationId;
        } else if (conversationId.length === 10) {
          destinationList[0] = `+1${conversationId}`;
        } else if (conversationId.length === 11) {
          destinationList[0] = `+${conversationId}`;
        }
        //rest service returns last number for bulk messages
        this.setActiveConversationId(destinationList[destinationList.length - 1]);
      } else if (this.newMessageGroupMode && destinationList && destinationList.length > 1) {
        for(let i = 0; i < destinationList.length; i++){
          if (destinationList[i].startsWith('+1')) {
            destinationList[i] = destinationList[i];
          } else if (destinationList[i].length === 10) {
            destinationList[i] = `+1${destinationList[i]}`;
          } else if (destinationList[i].length === 11) {
            destinationList[i] = `+${destinationList[i]}`;
          }
        }
        this.setActiveConversationId(destinationList.sort().join(""));
      }

      if (this.isMultimediaMessage) {
        this.sendMMSMessage(messageText, destinationList);
      } else {
        this.sendTextMessage(messageText, destinationList);
      }


    },

    sendTextMessage(messageText, destinationList) {
      const groupMessage = this.newMessageGroupMode || this.activeConversation?.isGroupConversation;
      const message = {
        from: this.activeNumber.number,
        body: messageText,
        originationTime: Date.now(),
        groupMessage
      }

      if (destinationList) {
        message.toList = destinationList;
        if (groupMessage) {
          message.to = destinationList.sort().join('');
        }
      } else {
        message.to = this.activeConversation.conversationId;
      }

      this.isLoading = true;
      this.sendSMS(new Message(message)).finally(() => {
        this.isLoading = false;
      });
    },

    dropLoadFiles(event) {
      event.preventDefault();
      const newFile = event.dataTransfer?.files[0];
      if (newFile) {
        this.loadFile(newFile);
      }
    },

    loadFile(file) {
      if (this.activeProvider.isValidAttachment(file)) {
        this.setSourceImg(file);
        this.changeAttachedContent({ hasFile: true, hasGif: false });
      } else {
        this.$refs.errorMessageDialog.openDialog(file);
      }
    },

    sendMMSMessage(messageText, destinationList) {
      if (this.attachedFile.isGif) {
        this.sendGif(messageText, destinationList);
      } else {
        this.sendFiles(messageText, destinationList);
      }
    },

    sendFiles(messageText, destinationList) {
      const formData = new FormData();
      const fileData = this.attachedFile.file.fileSRC;

      this.blobFile = {
        file: this.attachedFile.file.fileSRC,
        content: this.attachedFile.file.content,
        contentType: this.attachedFile.file.type
      };

      formData.append('file', fileData, this.attachedFile.file.name);
      formData.append('message', messageText);
      if (destinationList) {
        formData.append('toList', destinationList);
      } else {
        formData.append('to', this.activeConversation.conversationId);
      }

      if(this.newMessageGroupMode || this.activeConversation?.isGroupConversation){
        formData.append('groupMessage', true);
      }

      this.sendMMS(formData).then(() => {
        if (destinationList?.length === 1) {
          this.setActiveConversationId(this.activeConversation.conversationId);
        }
      });
    },

    sendGif(messageText, destinationList) {
      const gifFile = this.attachedFile.file.sourceImage.file;
      const formData = new FormData();

      this.blobFile =  { file: gifFile, contentType: gifFile.type };

      formData.append('file', gifFile, 'file.gif');
      formData.append('message', messageText);
      if (destinationList) {
        formData.append('toList', destinationList);
      } else {
        formData.append('to', this.activeConversation.conversationId);
      }

      if(this.newMessageGroupMode || this.activeConversation?.isGroupConversation){
        formData.append('groupMessage', true);
      }

      this.sendMMS(formData).then(() => {
        if (destinationList?.length === 1) {
          this.setActiveConversationId(this.activeConversation.conversationId);
        }
      });
    },

    addFile(file) {
      this.attachedFile = {
        file: {
          name: file.name,
          content: file.content,
          size: file.size,
          type: file.type,
          fileSRC: file.src
        },
        icon: this.selectIcon(file.name),
        hasFile: true
      };
    },

    setSourceImg(file) {
      const fr = new FileReader();
      fr.readAsDataURL(file);
      fr.onload = () => {
        this.attachedFile = {
          file: {
            name: file.name,
            size: file.size,
            type: file.type,
            sourceImage: fr.result,
            fileSRC: file
          },
          hasFile: true
        }
      };
    },

    handleEmojiSelect({ detail }) {
      this.$refs.textMessageInput.addEmoji(detail.unicode);
    },

    selectIcon(fileName) {
      const fileArray = fileName.split('.');
      if (fileArray[fileArray.length - 1] === 'vcf') return 'mdi-card-account-phone';
      return 'mdi-file-image';
    },

    preventDefault(event) {
      event.preventDefault();
      return false;
    },

    selectedGif(gif) {
      this.attachedFile = {
        file: {
          sourceImage: gif,
          type: 'image/gif',
        },
        hasFile: true,
        isGif: true
      };
      this.changeAttachedContent(cloneDeep(this.attachedFile));
    },

    clearLists() {
      this.attachedFile = { hasFile: false, hasGif: false };
      this.changeAttachedContent(cloneDeep(this.attachedFile));
    },

    hasMessageData() {
      const messageText = this.$refs.textMessageInput.messageText;
      //TODO: May need to investigate this...
      const sendNumber = this.activeConversation?.contact.number;
      return (!!messageText.trim() || !!this.isMultimediaMessage) && this.isValidPhoneNumber(sendNumber);
    },
  }
};
</script>

<style scoped>
.max-input-height {
  max-width: 800px;
  margin: 0 auto;
  height: 60px;
  position: relative;
}
</style>
